import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { usePermissions } from "../../PermissionsProvider";
import {
  EvaluateResult,
  ChatCompletionRequest,
  ChatCompletionResponse,
  ChatMessage,
  EvaluateResponse,
  Turn,
  EvaluateRequest,
  ToolCall,
} from "../../model_gen/chat";
import { SentinelService } from "../../services/SentinelService";
import { Sentinel } from "../../model_gen/sentinel";
import { v4 as uuidv4 } from "uuid";
import { RequestsService } from "../../services/RequestsService";
import EditSentinelForm from "../Sentinel/EditSentinelForm";
import { EvaluationService } from "../../services/EvaluationService";
import { TestSet } from "../../model_gen/evaluation";
import TestSetContextMenu from "../TestSet/TestSetContextMenu";
import { TestSetService } from "../../services/TestSetService";
import RequestOverview from "./RequestOverview";
import EvaluationBlocksList from "./EvaluationBlocksList";
import { IntentGroup } from "../../model_gen/intent";
import { IntentService } from "../../services/IntentService";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { MultiSelect, MultiSelectOption } from "../ui/multi-select";
import { 
  CopyIcon,
  PlayIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  PlusIcon,
  EditIcon,
  XIcon,
  Loader2Icon,
  SearchIcon,
  RotateCcwIcon,
  EyeIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { ConfigurationPanelRef } from "../../types/IApplication";
import { toast } from "sonner";

interface RequestOverviewProps {
  // Add any props if needed
}

interface ReRun {
  id: string;
  messages: ChatMessage[];
  state: "pre-run" | "post-run";
  model?: string;
  metrics?: {
    time: number;
    tokens: {
      prompt: number;
      completion: number;
    };
    cost: number;
  };
  originalRequest: ChatCompletionRequest;
  response?: ChatCompletionResponse;
}

interface EvaluationBlock {
  id: string;
  selectedSentinels: string[];
  editedSentinels: { [key: string]: Sentinel };
  versionId: string;
  hasRun: boolean;
  evaluationResponse?: EvaluateResponse;
  evaluationRunId?: number;
  evaluationRequestId?: string;
}

const RequestOverviewScreen: React.FC<RequestOverviewProps> = () => {
  const { apiKey, companyId } = usePermissions();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingRerun, setLoadingRerun] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [request, setRequest] = useState<ChatCompletionRequest | null>(null);
  const [response, setResponse] = useState<ChatCompletionResponse | null>(null);
  const { sessionId, applicationId, requestId } = useParams<{
    sessionId: string;
    applicationId: string;
    requestId: string;
  }>();
  const [evaluationBlocks, setEvaluationBlocks] = useState<EvaluationBlock[]>(
    []
  );
  const [reruns, setReruns] = useState<ReRun[]>([]);
  const [editingMessage, setEditingMessage] = useState<{
    rerunId: string;
    index: number;
  } | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string>("original");
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [isEditSentinelModalVisible, setIsEditSentinelModalVisible] =
    useState(false);
  const selectedSentinelRef = useRef<Sentinel | undefined>(undefined);
  const [, forceUpdate] = useState({});
  const [originalSentinels, setOriginalSentinels] = useState<Sentinel[]>([]);
  const [selectKey, setSelectKey] = useState(0);
  const [runningEvaluations, setRunningEvaluations] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingUrls, setLoadingUrls] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [dropdownMenus, setDropdownMenus] = useState<{
    [key: string]: React.ReactElement;
  }>({});
  const focusedEvaluationBlockRef = useRef<EvaluationBlock | null>(null);
  const [testSetsByAction, setTestSetsByAction] = useState<
    Record<string, TestSet[]>
  >({});
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);

  // Tool call state management
  const [editedToolCalls, setEditedToolCalls] = useState<{ [key: string]: ToolCall[] }>({});
  const [showAddToolCall, setShowAddToolCall] = useState<{ [key: string]: boolean }>({});
  const [newToolCall, setNewToolCall] = useState<{ [key: string]: { name: string; arguments: string } }>({});

  useEffect(() => {
    if (applicationId && requestId) {
      setIsLoading(true);
      loadRequest();
    }
  }, [applicationId, requestId, apiKey]);

  useEffect(() => {
    if (request?.action_type) {
      loadSentinels(request.action_type);
    }
  }, [request]);

  useEffect(() => {
    if (apiKey && applicationId && request?.application_action_id) {
      loadIntentGroup(Number(applicationId), request.application_action_id);
    }
  }, [apiKey, applicationId, request]);

  useEffect(() => {
    if (applicationId && sessionId && requestId) {
      setDropdownMenus({});
      setLoadingUrls({});
      setEvaluationBlocks([]);
      loadRequest();
      loadExistingReruns();
      loadExistingEvaluations();
    }
  }, [sessionId, applicationId, requestId, apiKey]);

  useEffect(() => {
    if (applicationId) {
      loadTestSets();
    }
  }, [applicationId, apiKey]);

  const loadRequest = () => {
    setIsLoading(true);
    // First get the request to get the application_action_id
    RequestsService.getRequestById(requestId!, () => {}, apiKey).subscribe(
      (req: ChatCompletionRequest) => {
        if (req) {
          // console.log(`req id: ${requestId}`);
          console.log(`req: ${JSON.stringify(req)}`);
          setRequest(req);
          RequestsService.getRequestResponseById(
            requestId,
            () => {},
            apiKey
          ).subscribe(
            (response: ChatCompletionResponse) => {
              setResponse(response);
              setIsLoading(false);
            },
            (error: any) => {
              console.error("Error fetching response:", error);
              setIsLoading(false);
            }
          );
        } else {
          console.error("Failed to load request - no request data found");
          toast.error("Failed to load request - no request data found");
          setIsLoading(false);
        }
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
        toast.error("Failed to load request. Please try again.");
      }
    );
  };

  const loadIntentGroup = (
    applicationId: number,
    applicationActionId: number
  ) => {
    IntentService.getIntentGroupByApplicationAndIntent(
      applicationId,
      applicationActionId,
      () => {},
      apiKey
    ).subscribe(
      (intentGroup: IntentGroup) => {
        setIntentGroup(intentGroup);
      },
      (error: any) => {
        console.error("Error fetching intent group:", error);
      }
    );
  };

  const loadSentinels = (actionType: string) => {
    if (applicationId) {
      SentinelService.getSentinelsByIntentName(
        actionType,
        null,
        apiKey
      ).subscribe(
        (fetchedSentinels: Sentinel[]) => {
          setOriginalSentinels(
            Array.isArray(fetchedSentinels) ? fetchedSentinels : []
          );
        },
        (error: any) => {
          console.error("Error fetching sentinels:", error);
          setOriginalSentinels([]);
          toast.error("Failed to load sentinels");
        }
      );
    } else {
      setOriginalSentinels([]);
    }
  };

  const copyRequestId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast.success("Request ID copied to clipboard");
  };

  const handleSentinelChange = (value: (string | number)[], blockId: string) => {
    const stringValues = value.map(v => v.toString());
    setEvaluationBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              selectedSentinels: stringValues,
              editedSentinels: stringValues.reduce((acc, sentinelId) => {
                if (block.editedSentinels[sentinelId]) {
                  acc[sentinelId] = block.editedSentinels[sentinelId];
                }
                return acc;
              }, {} as { [key: string]: Sentinel }),
            }
          : block
      )
    );
  };

  const getFinalSentinelForBlock = (
    blockId: string,
    sentinelId: string
  ): Sentinel => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    return (
      block?.editedSentinels[sentinelId] ||
      originalSentinels.find((s) => s.id.toString() === sentinelId) ||
      ({} as Sentinel)
    );
  };

  const handleRunEvaluation = (blockId: string) => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    console.log(`block: ${JSON.stringify(block)}`);
    if (block) {
      const finalSentinels = block.selectedSentinels.map((sentinelId) =>
        getFinalSentinelForBlock(blockId, sentinelId)
      );
      setRunningEvaluations((prev) => ({ ...prev, [blockId]: true }));

      // Get the request that this evaluation block references
      let evaluationRequest: ChatCompletionRequest;
      if (block.versionId === "original") {
        if (!request) {
          console.error("Original request not found");
          return;
        }
        // Create a deep copy of the request
        evaluationRequest = JSON.parse(
          JSON.stringify(request)
        ) as ChatCompletionRequest;
        // Add the assistant's response to the messages for evaluation
        if (
          request?.params?.messages?.[request?.params?.messages?.length - 1]
            .role === "assistant"
        ) {
          evaluationRequest.params.messages = [
            ...(evaluationRequest.params.messages || []),
            JSON.parse(
              JSON.stringify(
                request.params.messages[request.params.messages.length - 1]
              )
            ),
          ];
        }
      } else {
        const rerun = reruns.find((r) => r.id === block.versionId);
        if (!rerun) {
          console.error("Rerun not found");
          return;
        }
        // Create a deep copy of the request
        evaluationRequest = JSON.parse(
          JSON.stringify(rerun.originalRequest)
        ) as ChatCompletionRequest;
        evaluationRequest.params.messages = JSON.parse(
          JSON.stringify(rerun.messages)
        );
      }

      EvaluationService.runOneOffEvaluation(
        evaluationRequest,
        finalSentinels,
        null,
        apiKey
      ).subscribe(
        (response: EvaluateResponse) => {
          setRunningEvaluations((prev) => ({ ...prev, [blockId]: false }));
          setEvaluationBlocks((prev) =>
            prev.map((b) =>
              b.id === blockId
                ? { ...b, hasRun: true, evaluationResponse: response }
                : b
            )
          );
        },
        (error: any) => {
          console.error("Error running evaluation:", error);
          setRunningEvaluations((prev) => ({ ...prev, [blockId]: false }));
          toast.error("Failed to run evaluation. Please try again.");
        }
      );
    }
  };

  const handleAddEvaluation = () => {
    const highSeveritySentinels = originalSentinels
      .filter((sentinel) => sentinel.severity >= 3)
      .map((sentinel) => sentinel.id.toString());

    setEvaluationBlocks((prev) => [
      ...prev,
      {
        id: uuidv4(),
        selectedSentinels: highSeveritySentinels,
        editedSentinels: {},
        versionId: selectedVersion,
        hasRun: false,
        evaluationRequestId: `manual_${uuidv4()}`,
      },
    ]);
  };

  const handleRerunRequest = () => {
    if (!request) {
      console.error("Cannot create rerun: request not found");
      return;
    }

    const originalRequest = request;

    // Get all messages from the original request
    const originalMessages = originalRequest?.params?.messages || [];

    // Create a new rerun with all original messages except the last one if it's an assistant message
    const newRerun: ReRun = {
      id: `Rerun ${reruns.length + 1}`,
      messages: originalMessages, // Take all messages except the last one
      state: "pre-run",
      model: originalRequest.params.model,
      originalRequest: {
        ...originalRequest,
        params: {
          ...originalRequest.params,
          messages: originalMessages, // Update the request messages too
        },
      },
    };

    console.log("New rerun messages:", newRerun.messages);

    setReruns((prevReruns) => [...prevReruns, newRerun]);
    setSelectedVersion(newRerun.id);
  };

  const handleModelChange = (rerunId: string, newModel: string) => {
    setReruns((prevReruns) =>
      prevReruns.map((rerun) =>
        rerun.id === rerunId ? { ...rerun, model: newModel } : rerun
      )
    );
  };

  const handleEditMessage = (
    rerunId: string,
    index: number,
    newContent: string
  ) => {
    setReruns((prevReruns) =>
      prevReruns.map((rerun) =>
        rerun.id === rerunId
          ? {
              ...rerun,
              messages: rerun.messages.map((msg, i) =>
                i === index ? { ...msg, content: newContent } : msg
              ),
            }
          : rerun
      )
    );
  };

  // Tool call handlers
  const getToolCallKey = (rerunId: string, messageIndex: number) => `${rerunId}:${messageIndex}`;

  const handleToolCallChange = (
    rerunId: string,
    messageIndex: number,
    toolCallIndex: number,
    field: string,
    value: string
  ) => {
    const key = getToolCallKey(rerunId, messageIndex);
    const currentToolCalls = editedToolCalls[key] || [];
    
    const updatedToolCalls = [...currentToolCalls];
    if (field === "name") {
      updatedToolCalls[toolCallIndex].function.name = value;
    } else if (field === "arguments") {
      updatedToolCalls[toolCallIndex].function.arguments = value;
    }

    setEditedToolCalls(prev => ({
      ...prev,
      [key]: updatedToolCalls
    }));

    // Update the rerun messages with the new tool calls
    setReruns((prevReruns) =>
      prevReruns.map((rerun) =>
        rerun.id === rerunId
          ? {
              ...rerun,
              messages: rerun.messages.map((msg, i) =>
                i === messageIndex 
                  ? { ...msg, tool_calls: updatedToolCalls }
                  : msg
              ),
            }
          : rerun
      )
    );
  };

  const handleAddToolCall = (rerunId: string, messageIndex: number) => {
    const key = getToolCallKey(rerunId, messageIndex);
    
    // Initialize tool calls if not already done
    if (!editedToolCalls[key]) {
      const rerun = reruns.find(r => r.id === rerunId);
      const message = rerun?.messages[messageIndex];
      const currentToolCalls = message?.tool_calls || [];
      setEditedToolCalls(prev => ({
        ...prev,
        [key]: [...currentToolCalls]
      }));
    }
    
    setShowAddToolCall(prev => ({
      ...prev,
      [key]: true
    }));
    
    setNewToolCall(prev => ({
      ...prev,
      [key]: { name: "", arguments: "" }
    }));
  };

  const handleConfirmAddToolCall = (rerunId: string, messageIndex: number) => {
    const key = getToolCallKey(rerunId, messageIndex);
    const currentToolCalls = editedToolCalls[key] || [];
    const toolCallData = newToolCall[key] || { name: "", arguments: "" };

    const newToolCallObj: ToolCall = {
      id: `call_${Date.now()}`,
      type: "function",
      function: {
        name: toolCallData.name,
        arguments: toolCallData.arguments,
      },
    };

    const updatedToolCalls = [...currentToolCalls, newToolCallObj];
    
    setEditedToolCalls(prev => ({
      ...prev,
      [key]: updatedToolCalls
    }));

    // Update the rerun messages with the new tool calls
    setReruns((prevReruns) =>
      prevReruns.map((rerun) =>
        rerun.id === rerunId
          ? {
              ...rerun,
              messages: rerun.messages.map((msg, i) =>
                i === messageIndex 
                  ? { ...msg, tool_calls: updatedToolCalls }
                  : msg
              ),
            }
          : rerun
      )
    );

    setNewToolCall(prev => ({
      ...prev,
      [key]: { name: "", arguments: "" }
    }));
    
    setShowAddToolCall(prev => ({
      ...prev,
      [key]: false
    }));
  };

  const handleDeleteToolCall = (rerunId: string, messageIndex: number, toolCallIndex: number) => {
    const key = getToolCallKey(rerunId, messageIndex);
    const currentToolCalls = editedToolCalls[key] || [];
    
    const updatedToolCalls = currentToolCalls.filter((_, index) => index !== toolCallIndex);
    
    setEditedToolCalls(prev => ({
      ...prev,
      [key]: updatedToolCalls
    }));

    // Update the rerun messages with the new tool calls
    setReruns((prevReruns) =>
      prevReruns.map((rerun) =>
        rerun.id === rerunId
          ? {
              ...rerun,
              messages: rerun.messages.map((msg, i) =>
                i === messageIndex 
                  ? { ...msg, tool_calls: updatedToolCalls }
                  : msg
              ),
            }
          : rerun
      )
    );
  };

  const handleSetNewToolCall = (rerunId: string, messageIndex: number, toolCall: { name: string; arguments: string }) => {
    const key = getToolCallKey(rerunId, messageIndex);
    setNewToolCall(prev => ({
      ...prev,
      [key]: toolCall
    }));
  };

  const handleSetShowAddToolCall = (rerunId: string, messageIndex: number, show: boolean) => {
    const key = getToolCallKey(rerunId, messageIndex);
    setShowAddToolCall(prev => ({
      ...prev,
      [key]: show
    }));
  };

  const getToolCallsForMessage = (rerunId: string, messageIndex: number) => {
    const key = getToolCallKey(rerunId, messageIndex);
    return editedToolCalls[key] || [];
  };

  const getShowAddToolCallForMessage = (rerunId: string, messageIndex: number) => {
    const key = getToolCallKey(rerunId, messageIndex);
    return showAddToolCall[key] || false;
  };

  const getNewToolCallForMessage = (rerunId: string, messageIndex: number) => {
    const key = getToolCallKey(rerunId, messageIndex);
    return newToolCall[key] || { name: "", arguments: "" };
  };

  const renderVersionContent = () => {
    if (selectedVersion === "original") {
      return renderOriginalRequest();
    } else {
      const rerun = reruns.find((r) => r.id === selectedVersion);
      return rerun ? renderRerunRequest(rerun) : null;
    }
  };

  const renderOriginalRequest = () => {
    if (!request) {
      return <p className="text-sm text-muted-foreground">Request not found</p>;
    }

    return (
      <RequestOverview
        request={request}
        response={response}
        isEditable={false}
        intentGroupId={intentGroup?.id}
      />
    );
  };

  const handleRequestRerun = (rerun: ReRun) => {
    setLoadingRerun((prev) => ({ ...prev, [rerun.id]: true }));
    const newRequest: ChatCompletionRequest = rerun.originalRequest;
    newRequest.params.messages = rerun.messages;
    newRequest.params.model = rerun.model;
    newRequest.fallback_model = null;
    newRequest.preserve_model_alias = true;
    if (newRequest.metadata) {
      newRequest.metadata["rerun"] = "true";
      newRequest.metadata["original_request_id"] = requestId;
    } else {
      newRequest.metadata = { rerun: "true", original_request_id: requestId };
    }
    RequestsService.getChatCompletionRerun(
      () => {},
      newRequest,
      apiKey
    ).subscribe(
      (response: ChatCompletionResponse) => {
        setLoadingRerun((prev) => ({ ...prev, [rerun.id]: false }));
        console.log("Rerun response:", response);
        // Update the rerun state with the response
        setReruns((prevReruns) =>
          prevReruns.map((r) => {
            if (r.id === rerun.id) {
              return {
                ...r,
                state: "post-run",
                model: response.model,
                metrics: {
                  time: response.response_time,
                  tokens: {
                    prompt: response.usage.prompt_tokens,
                    completion: response.usage.completion_tokens,
                  },
                  cost: response.usage.cost,
                },
                messages: [...r.messages],
                response: response,
              };
            }
            return r;
          })
        );
      },
      (error: any) => {
        console.error(`Rerun error: ${JSON.stringify(error, null, 2)}`);
        setLoadingRerun((prev) => ({ ...prev, [rerun.id]: false }));
        toast.error("Failed to rerun the request. Please try again.");
      }
    );
  };

  const renderRerunRequest = (rerun: ReRun) => {
    return (
      <div className="space-y-6">
        {loadingRerun[rerun.id] ? (
          <div className="flex justify-center items-center py-12">
            <Loader2Icon className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <RequestOverview
              request={{
                ...rerun.originalRequest,
                params: {
                  ...rerun.originalRequest.params,
                  messages: rerun.messages,
                  model: rerun.model,
                },
              }}
              response={rerun.response}
              isEditable={rerun.state === "pre-run"}
              onMessageEdit={(index, content) =>
                handleEditMessage(rerun.id, index, content)
              }
              onModelChange={(model) => handleModelChange(rerun.id, model)}
              intentGroupId={intentGroup?.id}
              editingMessageIndex={
                editingMessage?.rerunId === rerun.id
                  ? editingMessage.index
                  : null
              }
              onEditingMessageChange={(index) =>
                setEditingMessage(
                  index !== null ? { rerunId: rerun.id, index } : null
                )
              }
              editMode={rerun.state === "pre-run" ? "all" : "final-assistant"}
              onToolCallChange={(toolCallIndex, field, value) => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                handleToolCallChange(rerun.id, messageIndex, toolCallIndex, field, value);
              }}
              onAddToolCall={() => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                handleAddToolCall(rerun.id, messageIndex);
              }}
              onConfirmAddToolCall={() => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                handleConfirmAddToolCall(rerun.id, messageIndex);
              }}
              onDeleteToolCall={(toolCallIndex) => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                handleDeleteToolCall(rerun.id, messageIndex, toolCallIndex);
              }}
              showAddToolCall={(() => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                return getShowAddToolCallForMessage(rerun.id, messageIndex);
              })()}
              newToolCall={(() => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                return getNewToolCallForMessage(rerun.id, messageIndex);
              })()}
              setNewToolCall={(toolCall) => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                handleSetNewToolCall(rerun.id, messageIndex, toolCall);
              }}
              setShowAddToolCall={(show) => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                handleSetShowAddToolCall(rerun.id, messageIndex, show);
              }}
              editedToolCalls={(() => {
                const messageIndex = editingMessage?.rerunId === rerun.id ? editingMessage.index : 0;
                return getToolCallsForMessage(rerun.id, messageIndex);
              })()}
            />
            {rerun.state === "pre-run" && (
              <Button
                onClick={() => handleRequestRerun(rerun)}
                disabled={loadingRerun[rerun.id]}
                className="w-full gap-2"
              >
                {loadingRerun[rerun.id] ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <PlayIcon className="w-4 h-4" />
                )}
                Run Request
              </Button>
            )}
          </>
        )}
      </div>
    );
  };

  const loadExistingReruns = () => {
    RequestsService.getRequestReruns(
      sessionId!,
      applicationId!,
      requestId!,
      null,
      apiKey
    ).subscribe(
      (fetchedReruns: Turn[]) => {
        const convertedReruns: ReRun[] = fetchedReruns.map(
          (turn: Turn, index: number) => {
            // Use the request messages directly - they already contain the response
            const messages = turn.request?.params?.messages || [];

            const rerun: ReRun = {
              id: `Rerun ${index + 1}`,
              messages: messages.map((msg) => ({ ...msg })), // Create a deep copy of messages
              state: "post-run" as const,
              model: turn.response?.model,
              metrics: {
                time: turn.response?.response_time || 0,
                tokens: {
                  prompt: turn.response?.usage?.prompt_tokens || 0,
                  completion: turn.response?.usage?.completion_tokens || 0,
                },
                cost: turn.response?.usage?.cost || 0,
              },
              originalRequest: turn.request,
              response: turn.response,
            };

            console.log("Created rerun:", {
              id: rerun.id,
              messages: rerun.messages,
            });

            return rerun;
          }
        );
        setReruns(convertedReruns);
      },
      (error: any) => {
        console.error("Error fetching existing reruns:", error);
        toast.error("Failed to load existing reruns");
      }
    );
  };

  const selectedRerun = reruns.find((r) => r.id === selectedVersion);

  const handleEditSentinel = (
    e: React.MouseEvent,
    block: EvaluationBlock,
    sentinelId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEvaluationRunning()) return; // Prevent editing if any evaluation is running

    if (!block) {
      console.error("Evaluation block not found for sentinel:", sentinelId);
      console.error("Unable to edit: Evaluation block not found.");
      return;
    }

    const sentinel =
      block.editedSentinels[sentinelId] ||
      originalSentinels.find((s) => s.id.toString() === sentinelId);
    if (sentinel) {
      selectedSentinelRef.current = sentinel;
      focusedEvaluationBlockRef.current = block;
      setIsEditSentinelModalVisible(true);
      forceUpdate({}); // Force a re-render
    } else {
      console.error("Sentinel not found for id:", sentinelId);
      console.error("Unable to edit: Sentinel not found.");
    }
  };

  const handleEditSentinelSubmit = (formData: Sentinel) => {
    if (formData.id === undefined) {
      console.error("Sentinel ID is undefined in formData:", formData);
      console.error("Unable to save changes: Sentinel ID is missing.");
      return;
    }

    setEvaluationBlocks((prevBlocks) =>
      prevBlocks.map((block) => {
        if (block.selectedSentinels.includes(formData.id.toString())) {
          return {
            ...block,
            editedSentinels: {
              ...block.editedSentinels,
              [formData.id]: formData,
            },
          };
        }
        return block;
      })
    );
    setSelectKey((prevKey) => prevKey + 1); // Force Select to re-render
    // Success feedback would be handled by a toast system

    // Close the modal
    setIsEditSentinelModalVisible(false);
    focusedEvaluationBlockRef.current = null;
    selectedSentinelRef.current = undefined;
  };

  const isSentinelEdited = (blockId: string, sentinelId: string): boolean => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    const editedSentinel = block?.editedSentinels[sentinelId];
    if (!editedSentinel) return false;

    const originalSentinel = originalSentinels.find(
      (s) => s.id.toString() === sentinelId
    );
    if (!originalSentinel) return false;

    return (
      editedSentinel.sentinel_name !== originalSentinel.sentinel_name ||
      editedSentinel.sentinel_directive.directive_body.main !==
        originalSentinel.sentinel_directive.directive_body.main ||
      editedSentinel.sentinel_directive.directive_body.override !==
        originalSentinel.sentinel_directive.directive_body.override ||
      !arraysEqual(
        editedSentinel.action_types,
        originalSentinel.action_types
      ) ||
      editedSentinel.severity !== originalSentinel.severity ||
      !objectsEqual(
        editedSentinel.meta as { [key: string]: string },
        originalSentinel.meta as { [key: string]: string }
      )
    );
  };

  // Helper functions
  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((val, index) => val === b[index]);

  const objectsEqual = (
    a: { [key: string]: string },
    b: { [key: string]: string }
  ) =>
    Object.keys(a).length === Object.keys(b).length &&
    Object.keys(a).every((key) => a[key] === b[key]);

  const getSentinelName = (blockId: string, sentinelId: string): string => {
    const block = evaluationBlocks.find((b) => b.id === blockId);
    const editedSentinel = block?.editedSentinels[sentinelId];
    if (editedSentinel) {
      return editedSentinel.sentinel_name;
    }
    const originalSentinel = originalSentinels.find(
      (s) => s.id.toString() === sentinelId
    );
    console.log(`originalSentinel: ${JSON.stringify(originalSentinel)}`);
    return originalSentinel ? originalSentinel.sentinel_name : "";
  };

  const isLastMessageAssistant = () => {
    if (selectedVersion === "original") {
      const responseContent =
        request?.params?.messages?.[request?.params?.messages?.length - 1]
          .role === "assistant"
          ? request?.params?.messages?.[request?.params?.messages?.length - 1]
              .content
          : null;
      return responseContent !== undefined && responseContent !== "";
    } else {
      const rerun = reruns.find((r) => r.id === selectedVersion);
      return (
        rerun?.messages.length > 0 &&
        rerun.messages[rerun.messages.length - 1].role === "assistant"
      );
    }
  };

  const renderSelectedSentinel = (blockId: string, sentinelId: string) => {
    const isEdited = isSentinelEdited(blockId, sentinelId);
    const sentinelName = getSentinelName(blockId, sentinelId);
    const isRunning = runningEvaluations[blockId] || isEvaluationRunning();
    const block = evaluationBlocks.find((b) => b.id === blockId);
    const hasRun = block?.hasRun || false;

    return (
      <div className="inline-flex items-center gap-2 bg-muted px-2 py-1 rounded text-sm">
        <span>
          {sentinelName}
          {isEdited && (
            <span className="text-maitai-green font-bold ml-1">*</span>
          )}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-4 w-4 p-0"
          onClick={(e) => handleEditSentinel(e, block!, sentinelId)}
          disabled={isRunning}
        >
          {hasRun ? <SearchIcon className="w-3 h-3" /> : <EditIcon className="w-3 h-3" />}
        </Button>
      </div>
    );
  };

  const hasUnrunEvaluations = () => {
    return evaluationBlocks
      .filter((block) => block.versionId === selectedVersion)
      .some((block) => !block.hasRun); // Assuming we add a 'hasRun' property to EvaluationBlock
  };

  const isEvaluationRunning = () =>
    Object.values(runningEvaluations).some((running) => running);



  const handleViewRequest = (
    evaluationRequestId: string,
    sentinelId: string
  ) => {
    const key = `${evaluationRequestId}:${sentinelId}`;

    const fetchUrls = () => {
      if (!dropdownMenus[key]) {
        setLoadingUrls((prev) => ({ ...prev, [key]: true }));
        EvaluationService.getEvaluationRequestUrls(
          evaluationRequestId,
          sentinelId,
          null,
          apiKey
        ).subscribe(
          (response: any[]) => {
            setLoadingUrls((prev) => ({ ...prev, [key]: false }));
            // For now, just navigate to the first URL
            if (response.length > 0) {
              navigate(response[0].url, { state: {} });
            }
          },
          (error: any) => {
            console.error("Error fetching evaluation request URLs:", error);
            toast.error("Failed to fetch request details. Please try again.");
            setLoadingUrls((prev) => ({ ...prev, [key]: false }));
          }
        );
      }
    };

    return (
      <Button
        variant="outline"
        size="sm"
        onClick={fetchUrls}
        disabled={loadingUrls[key]}
        className="gap-2"
      >
        {loadingUrls[key] ? (
          <Loader2Icon className="w-3 h-3 animate-spin" />
        ) : (
          <EyeIcon className="w-3 h-3" />
        )}
        View Request
      </Button>
    );
  };

  const loadExistingEvaluations = () => {
    EvaluationService.getEvaluationRequests(
      applicationId!,
      sessionId!,
      requestId!,
      null,
      apiKey
    ).subscribe(
      (requests: EvaluateRequest[]) => {
        const newBlocks: EvaluationBlock[] = requests
          .filter((request) => request.eval_results_set)
          .map((request) => {
            const sentinelIds = request.eval_results_set.evaluation_results.map(
              (result: EvaluateResult) => result.sentinel_id.toString()
            );

            return {
              id: uuidv4(),
              selectedSentinels: sentinelIds,
              editedSentinels: {},
              versionId: "original",
              hasRun: true,
              evaluationResponse: request.eval_results_set,
              evaluationRunId: request.evaluation_run_id,
              evaluationRequestId: request.evaluation_request_id,
            };
          });

        setEvaluationBlocks(newBlocks);
      },
      (error: any) => {
        console.error("Error fetching existing evaluations:", error);
        toast.error("Failed to load existing evaluations");
      }
    );
  };

  const canAddEvaluation = () => {
    return isLastMessageAssistant() &&
      !hasUnrunEvaluations() &&
      !isEvaluationRunning() &&
      originalSentinels.length > 0;
  };

  const currentRequest = selectedVersion === "original" ? request : null;

  const loadTestSets = () => {
    TestSetService.getTestSets(null, apiKey).subscribe(
      (response: any) => {
        // Group test sets by application_action_id
        const testSets = response || [];
        const groupedTestSets = testSets.reduce(
          (acc: Record<string, TestSet[]>, testSet: TestSet) => {
            const actionType = testSet?.intent_group?.intent_name;
            if (!acc[actionType]) {
              acc[actionType] = [];
            }
            acc[actionType].push(testSet);
            return acc;
          },
          {}
        );

        setTestSetsByAction(groupedTestSets);
      },
      (error: any) => {
        console.error("Failed to load test sets:", error);
        toast.error("Failed to load test sets");
      }
    );
  };

  const handleTestSetUpdate = () => {
    setRequest((prevRequest) => {
      if (!prevRequest) return prevRequest;
      loadTestSets();
      return prevRequest;
    });
  };

  const getExistingTestSetIds = (metadata?: any) => {
    try {
      return metadata?.test_set_ids
        ? JSON.parse(metadata.test_set_ids).map(String)
        : [];
    } catch (e) {
      console.error("Error parsing test_set_ids:", e);
      return [];
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="mb-6">
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-5 w-96" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-8" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/applications")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Applications
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/application/${applicationId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Application
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/application/${applicationId}/intent/${request?.application_action_id}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {request?.action_type}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${intentGroup?.id}/session/${sessionId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Session
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Request</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Request Overview
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Analyze request details, run evaluations, and test different versions
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Card className="border-border">
          <CardContent className="p-8 space-y-8">
            
            {/* Request Info Card */}
            <Card className="bg-accent/20 border-border">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-gc-med text-foreground">
                    {selectedVersion === "original" ? "Original Request" : selectedVersion}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {currentRequest && (
                      <TestSetContextMenu
                        applicationId={applicationId}
                        applicationActionType={currentRequest.action_type || ""}
                        requestId={requestId || ""}
                        testSets={
                          currentRequest.action_type
                            ? [
                                ...(testSetsByAction[
                                  currentRequest.action_type
                                ] || []),
                              ].sort((a, b) => b.id - a.id)
                            : []
                        }
                        existingTestSetIds={getExistingTestSetIds(
                          currentRequest.metadata
                        )}
                        onUpdate={handleTestSetUpdate}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="gap-2"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add to Test Set
                        </Button>
                      </TestSetContextMenu>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground font-gc-reg">Request ID</p>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                        {(() => {
                          const fullId = selectedRerun
                            ? selectedRerun.response?.request_id ||
                              selectedRerun.originalRequest.request_id
                            : requestId;
                          return `...${fullId?.slice(-8)}`;
                        })()}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() =>
                          copyRequestId(
                            selectedRerun
                              ? selectedRerun.response?.request_id ||
                                selectedRerun.originalRequest.request_id
                              : requestId || ""
                          )
                        }
                      >
                        <CopyIcon className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground font-gc-reg">Date Created</p>
                    <p className="text-sm font-gc-reg text-foreground">
                      {moment(
                        selectedRerun
                          ? selectedRerun.originalRequest.date_created
                          : request?.date_created
                      ).format("MMM D, YYYY HH:mm:ss")}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground font-gc-reg">Action Type</p>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                      {selectedRerun
                        ? selectedRerun.originalRequest.action_type
                        : request?.action_type}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Request Content */}
            <div>{renderVersionContent()}</div>

            {/* Evaluation Blocks */}
            <EvaluationBlocksList
              evaluationBlocks={evaluationBlocks}
              selectedVersion={selectedVersion}
              originalSentinels={originalSentinels}
              selectKey={selectKey}
              runningEvaluations={runningEvaluations}
              companyId={companyId}
              canAddEvaluation={canAddEvaluation()}
              onSentinelChange={handleSentinelChange}
              onRunEvaluation={handleRunEvaluation}
              onAddEvaluation={handleAddEvaluation}
              onViewRequest={handleViewRequest}
              isEvaluationRunning={isEvaluationRunning}
            />
          </CardContent>
        </Card>
      </div>

      {/* Version Selection Panel */}
      <div
        className={cn(
          "fixed right-0 top-16 bottom-0 bg-background border-l border-border shadow-lg transition-transform duration-300 z-50",
          isPanelExpanded ? "w-80 translate-x-0" : "w-8 translate-x-0"
        )}
        onMouseEnter={() => setIsPanelExpanded(true)}
        onMouseLeave={() => setIsPanelExpanded(false)}
      >
        <div className="flex h-full">
          {/* Always visible tab/trigger */}
          <div className="w-8 bg-background border-r border-border flex flex-col items-center justify-center hover:bg-accent/50 transition-colors cursor-pointer">
            <div className="flex flex-col items-center gap-4">
              {isPanelExpanded ? (
                <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronLeftIcon className="w-4 h-4 text-muted-foreground" />
              )}
              {/* Vertical text when collapsed */}
              {!isPanelExpanded && (
                <div className="transform -rotate-90 text-xs text-muted-foreground whitespace-nowrap origin-center font-gc-reg">
                  Versions
                </div>
              )}
            </div>
          </div>
          
          {/* Panel content - only visible when expanded */}
          <div className={cn(
            "flex-1 p-6 overflow-y-auto transition-opacity duration-300",
            isPanelExpanded ? "opacity-100" : "opacity-0 pointer-events-none"
          )}>
            <h4 className="text-lg font-gc-med text-foreground mb-4">Versions</h4>
            <div className="space-y-2">
              <button
                onClick={() => setSelectedVersion("original")}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-md text-sm font-gc-reg transition-colors",
                  selectedVersion === "original"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                Original
              </button>
              {reruns.map((rerun) => (
                <button
                  key={rerun.id}
                  onClick={() => setSelectedVersion(rerun.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md text-sm font-gc-reg transition-colors",
                    selectedVersion === rerun.id
                      ? "bg-maitai-green/10 text-maitai-green"
                      : "text-foreground hover:bg-accent/50"
                  )}
                >
                  {rerun.id}
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              onClick={handleRerunRequest}
              className="w-full mt-4 gap-2 justify-start"
            >
              <PlusIcon className="w-4 h-4" />
              New Rerun
            </Button>
          </div>
        </div>
      </div>

      {/* Edit Sentinel Modal */}
      <Dialog
        open={isEditSentinelModalVisible}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditSentinelModalVisible(false);
            focusedEvaluationBlockRef.current = null;
            selectedSentinelRef.current = undefined;
          }
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-gc-med">Sentinel</DialogTitle>
          </DialogHeader>
          {selectedSentinelRef.current && (
            <EditSentinelForm
              key={`${focusedEvaluationBlockRef.current?.id}-${selectedSentinelRef.current.id}`}
              sentinel={selectedSentinelRef.current}
              loading={false}
              isDataLoaded={true}
              onSubmit={handleEditSentinelSubmit}
              onClose={() => {
                setIsEditSentinelModalVisible(false);
                focusedEvaluationBlockRef.current = null;
                selectedSentinelRef.current = undefined;
              }}
              readOnly={
                focusedEvaluationBlockRef.current
                  ? focusedEvaluationBlockRef.current?.hasRun
                  : false
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestOverviewScreen;
